import React, { useState } from "react";

/**
 * Decorative animated overlays for the welcome cover.
 * Pure CSS — no extra deps. Layer over the existing background style.
 * Pass `kind`: "particles" | "parallax" | "none".
 * Optional `compact` for small previews.
 */
export default function CoverAnimation({ kind = "none", compact = false }) {
  if (kind === "particles") return <Particles compact={compact} />;
  if (kind === "parallax") return <Parallax />;
  return null;
}

/* ---------------- Particles ---------------- */
function generateDots(count) {
  return Array.from({ length: count }).map((_, i) => {
    const size = 3.6 + Math.random() * 6.8;
    return {
      id: i,
      size,
      left: Math.random() * 100,
      top: Math.random() * 100,
      duration: 14 + Math.random() * 22,
      delay: -Math.random() * 30,
      opacity: 0.35 + Math.random() * 0.5,
      drift: (Math.random() * 60 - 30).toFixed(1),
    };
  });
}

function Particles({ compact }) {
  const count = compact ? 28 : 80;
  // Lazy initializer — computed once per mount, immutable thereafter.
  const [dots] = useState(() => generateDots(count));

  return (
    <div
      aria-hidden
      data-testid="cover-anim-particles"
      style={{
        position: "absolute", inset: 0, overflow: "hidden",
        pointerEvents: "none", zIndex: 1,
      }}
    >
      <style>{`
        @keyframes e650Float {
          0%   { transform: translate3d(0, 0, 0) scale(1); opacity: var(--e650-opacity, 0.4); }
          50%  { transform: translate3d(var(--e650-drift, 0px), -60vh, 0) scale(1.15); opacity: calc(var(--e650-opacity, 0.4) * 1.4); }
          100% { transform: translate3d(0, -120vh, 0) scale(0.85); opacity: 0; }
        }
        .e650-dot { position: absolute; border-radius: 9999px;
          background: radial-gradient(circle at 35% 30%, rgba(255,255,255,0.95), rgba(110,118,130,0.7) 60%, rgba(60,68,80,0.25) 100%);
          box-shadow: 0 0 6px rgba(40,48,60,0.35), 0 1px 2px rgba(0,0,0,0.15);
          animation-name: e650Float; animation-timing-function: linear; animation-iteration-count: infinite; }
      `}</style>
      {dots.map((d) => (
        <span
          key={d.id}
          className="e650-dot"
          style={{
            width: d.size, height: d.size,
            left: `${d.left}%`, top: `${d.top}%`,
            animationDuration: `${d.duration}s`,
            animationDelay: `${d.delay}s`,
            "--e650-opacity": d.opacity,
            "--e650-drift": `${d.drift}px`,
          }}
        />
      ))}
    </div>
  );
}

/* ---------------- Parallax (drifting gradient blobs) ---------------- */
function Parallax() {
  return (
    <div
      aria-hidden
      data-testid="cover-anim-parallax"
      style={{
        position: "absolute", inset: 0, overflow: "hidden",
        pointerEvents: "none", zIndex: 1,
      }}
    >
      <style>{`
        @keyframes e650Blob1 {
          0%   { transform: translate3d(-20%, -10%, 0) scale(1); }
          50%  { transform: translate3d(15%, 5%, 0) scale(1.15); }
          100% { transform: translate3d(-20%, -10%, 0) scale(1); }
        }
        @keyframes e650Blob2 {
          0%   { transform: translate3d(20%, 10%, 0) scale(1.1); }
          50%  { transform: translate3d(-10%, -5%, 0) scale(0.95); }
          100% { transform: translate3d(20%, 10%, 0) scale(1.1); }
        }
        @keyframes e650Blob3 {
          0%   { transform: translate3d(0, 20%, 0) scale(0.9); }
          50%  { transform: translate3d(-15%, -15%, 0) scale(1.2); }
          100% { transform: translate3d(0, 20%, 0) scale(0.9); }
        }
        .e650-blob {
          position: absolute; border-radius: 50%; filter: blur(80px);
          will-change: transform; mix-blend-mode: screen;
        }
      `}</style>
      <span
        className="e650-blob"
        style={{
          width: "55vw", height: "55vw", top: "-15%", left: "-10%",
          background: "radial-gradient(circle, rgba(204,255,0,0.30) 0%, transparent 65%)",
          animation: "e650Blob1 28s ease-in-out infinite",
        }}
      />
      <span
        className="e650-blob"
        style={{
          width: "50vw", height: "50vw", bottom: "-15%", right: "-10%",
          background: "radial-gradient(circle, rgba(255,140,60,0.25) 0%, transparent 65%)",
          animation: "e650Blob2 34s ease-in-out infinite",
        }}
      />
      <span
        className="e650-blob"
        style={{
          width: "40vw", height: "40vw", top: "30%", left: "30%",
          background: "radial-gradient(circle, rgba(255,80,140,0.18) 0%, transparent 65%)",
          animation: "e650Blob3 40s ease-in-out infinite",
        }}
      />
    </div>
  );
}
